import { Location } from '@angular/common';
import { Component, computed, OnInit, Signal, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BaseComponent } from '@shared/base/components/base-component';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { ForbiddenService } from '@shared/services/local/forbidden.service';
import { NotFoundService } from '@shared/services/local/not-found.service';
import { filter } from 'rxjs';

import { IconRegistryService } from '../../../mindpop/shared/services/icon-registry.service';
import { FeedPositionService } from '../../../mindpop/shared/state/feed-position.service';
import { ThemeService } from '../../../mindpop/shared/state/theme.service';
import { AppRoute } from '../../../shared/app.route.enum';

/**
 * A bottom navigation tab of the app layout.
 */
interface AppNavigationTab {
    key: string;
    route: string;
    icon: string;
    label: string;
    /** Whether the tab is dark (full-screen feed) and needs a dark tab bar. */
    dark: boolean;
}

/**
 * The mobile-first layout of the Mindpop app: a content area and a bottom tab bar
 * (Profile, PopMix, For You, Bib, Search), like the main pager of the native app.
 */
@Component({
    selector: 'intello-app-layout',
    standalone: true,
    imports: [RouterModule, MatIconModule, TranslateModule, MatTooltipModule],
    templateUrl: './app-layout.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./app-layout.component.scss']
})
export class AppLayoutComponent extends BaseComponent implements OnInit {

    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    readonly tabs: AppNavigationTab[] = [
        { key: 'profile', route: '/profile', icon: 'mp-user_circle_new', label: 'Mindpop.Tabs.Profile', dark: true },
        { key: 'revision', route: '/revision', icon: 'mp-revise', label: 'Mindpop.Tabs.Revision', dark: true },
        { key: 'for-you', route: '/for-you', icon: 'mp-mindpop_logo_big', label: 'Mindpop.Tabs.Home', dark: true },
        { key: 'library', route: '/library', icon: 'mp-book_new', label: 'Mindpop.Tabs.Bib', dark: true },
        { key: 'search', route: '/search', icon: 'mp-search', label: 'Mindpop.Tabs.Search', dark: true }
    ];

    readonly url: WritableSignal<string> = signal('');

    readonly isForbidden: WritableSignal<boolean> = signal(false);

    readonly notFound: WritableSignal<boolean> = signal(false);

    // -----------------------------------------------------------------------------------------------------
    // @ COMPUTED VARIABLES
    // -----------------------------------------------------------------------------------------------------

    readonly activeTab: Signal<AppNavigationTab> = computed(() => {
        const url = this.url();

        return this.tabs.find((tab: AppNavigationTab) => url === tab.route || url.startsWith(`${tab.route}/`) || url.startsWith(`${tab.route}?`)) ?? null;
    });

    /** Full-screen pages (feeds) render the tab bar transparent over a dark background. */
    readonly isDark: Signal<boolean> = computed(() => this.activeTab()?.dark === true || this.url().startsWith('/feed/'));

    /** The tab bar is hidden on nested feed pages to give the pop the whole screen. */
    readonly hideTabs: Signal<boolean> = computed(() => this.url().startsWith('/feed/') || this.url().startsWith('/onboarding'));

    // -----------------------------------------------------------------------------------------------------
    // @ CONSTRUCTOR
    // -----------------------------------------------------------------------------------------------------

    constructor(
        private router: Router,
        private location: Location,
        private forbiddenService: ForbiddenService,
        private notFoundService: NotFoundService,
        private iconRegistryService: IconRegistryService,
        private authenticationService: AuthenticationService,
        private feedPositionService: FeedPositionService,
        // Injected so the stored theme is applied as soon as the app shell renders
        private themeService: ThemeService
    ) {
        super();
        this.iconRegistryService.register();
    }

    /**
     * Tapping the tab of the screen that is already open scrolls its feed back to the top
     * (like the native app's "tap home again" behavior).
     *
     * @param tab - The tapped tab.
     * @returns {void}
     */
    onTabClick(tab: AppNavigationTab): void {
        if (this.isActive(tab) && (tab.key === 'for-you' || tab.key === 'pop-mix')) {
            this.feedPositionService.requestScrollToTop(tab.key);
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ LIFE CYCLE HOOKS
    // -----------------------------------------------------------------------------------------------------

    /** Logout of the desktop rail (the same as the settings: local state is dropped, the session ends). */
    logout(): void {
        try {
            localStorage.clear();
        } catch {
            // Storage may be unavailable
        }

        this.authenticationService.logout().subscribe();
    }

    ngOnInit(): void {
        this.url.set(this.router.url);

        super.addSubscription(
            this.router.events
                .pipe(filter((event) => event instanceof NavigationEnd))
                .subscribe((event: NavigationEnd) => {
                    this.url.set(event.urlAfterRedirects ?? event.url);
                    this.forbiddenService.allow();
                    this.notFoundService.notify(false);
                })
        );

        super.addSubscription(this.forbiddenService.isForbidden$.subscribe((value: boolean) => this.isForbidden.set(value)));

        super.addSubscription(
            this.notFoundService.notFound$.subscribe((value: boolean) => {
                this.notFound.set(value);

                if (value === true) {
                    this.location.replaceState(`/${AppRoute.App}`);
                }
            })
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    isActive(tab: AppNavigationTab): boolean {
        return this.activeTab()?.key === tab.key;
    }
}
